import { ButtonContent } from '../../ui/ButtonContent';
/**
 * Organization settings section for the settings page.
 *
 * Allows users with `organization:manage_settings` permission to edit
 * the organization's name, slug, logo, and website URL.
 *
 * @module components/admin/settings/OrgSettingsSection
 */

import React, { useState, useEffect } from 'react';
import { useOrg } from '../../../lib/OrgProvider';
import { useSupabase } from '../../../lib/SupabaseProvider';
import { captureException } from '../../../lib/telemetry/posthog/frontend';
import { useOrgCredits } from '../../../lib/useOrgCredits';
import { useEntitlements } from '../../../lib/useEntitlements';
import { IconBuilding } from '@tabler/icons-react';
import { Toggle } from '../../ui/Toggle';
import { FeatureGateBadge } from '../../ui/FeatureGateBadge';
import { Tooltip } from '../../Tooltip';
import { CopyIcon } from '../../icons/CopyIcon';
import { CheckIcon } from '../../icons/CheckIcon';
import { AnimatedLabel } from '../../ui/AnimatedLabel';
import { AnimatedPresence } from '../../ui/AnimatedPresence';

/**
 * Organization settings form section.
 * Reads from the active org and allows editing name, slug, website, logo.
 */
export const OrgSettingsSection: React.FC = () => {
  const { activeOrg, refresh, hasPermission } = useOrg();
  const { supabase } = useSupabase();
  const { credits, loading: creditsLoading, error: creditsError } = useOrgCredits();
  const { can } = useEntitlements();

  /** Viewer/editor have `organization:view_settings` but not manage; admin
   *  and owner have both. Keep the fields visible (read-only) for everyone
   *  who can view, and only allow edits when manage is granted. */
  const canManageSettings = hasPermission('organization:manage_settings');
  const readOnlyTooltip = canManageSettings
    ? ''
    : "You have read-only access. Ask an owner or admin to change these.";

  const [name, setName] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [showHolitoReferral, setShowHolitoReferral] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [idCopied, setIdCopied] = useState(false);

  /** Copy the active org id to the clipboard with a brief confirmation. */
  const handleCopyId = async () => {
    if (!activeOrg) return;
    try {
      await navigator.clipboard.writeText(activeOrg.id);
      setIdCopied(true);
      setTimeout(() => setIdCopied(false), 2000);
    } catch (err) {
      captureException(
        err instanceof Error ? err : new Error(String(err)),
        'OrgSettingsSection.handleCopyId',
        { orgId: activeOrg.id },
      );
    }
  };

  useEffect(() => {
    if (activeOrg) {
      setName(activeOrg.name || '');
      setWebsiteUrl(activeOrg.website_url || '');
      setLogoUrl(activeOrg.logo_url || '');
      // Read referral setting from org settings jsonb (defaults to true)
      const settings = activeOrg.settings as Record<string, unknown> | null;
      setShowHolitoReferral(
        can('remove_holito_referral')
          ? settings?.show_holito_referral !== false
          : true,
      );
    }
  }, [activeOrg, can]);

  /** Save org settings to the database. */
  const handleSave = async () => {
    if (!activeOrg) return;
    setSaving(true);
    setSaveMessage(null);

    try {
      // Merge new settings with existing org settings (preserve other keys)
      const existingSettings = (activeOrg.settings as Record<string, unknown>) || {};
      const mergedSettings = {
        ...existingSettings,
        show_holito_referral: can('remove_holito_referral')
          ? showHolitoReferral
          : true,
      };

      const { error } = await supabase
        .from('organizations')
        .update({
          name,
          website_url: websiteUrl || null,
          logo_url: logoUrl || null,
          settings: mergedSettings,
          updated_at: new Date().toISOString(),
        })
        .eq('id', activeOrg.id);

      if (error) {
        captureException(
          new Error(error.message),
          'OrgSettingsSection.handleSave',
          { orgId: activeOrg.id },
        );
        setSaveMessage('Failed to save. Please try again.');
      } else {
        setSaveMessage('Organization settings saved.');
        refresh();
      }
    } catch (err) {
      captureException(
        err instanceof Error ? err : new Error(String(err)),
        'OrgSettingsSection.handleSave',
        { orgId: activeOrg.id },
      );
      setSaveMessage('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!activeOrg) {
    return (
      <div className="text-muted-foreground text-sm">
        No organization selected.
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <IconBuilding className="w-6 h-6 text-muted-foreground" />
        <h2 className="text-lg font-semibold text-foreground">Organization</h2>
      </div>

      {/* Credits */}
      <div className="mb-6 p-4 bg-surface rounded-lg border border-border light:border-transparent">
        <div className="text-xs text-muted-foreground uppercase tracking-wider font-medium mb-2">
          Audio/video credits
        </div>
        {credits ? (
          <div className="flex items-baseline gap-4">
            <div>
              <span className="text-2xl font-bold text-foreground">{credits.available_credits}</span>
              <span className="text-sm text-muted-foreground ml-1">available</span>
            </div>
            {credits.reserved_credits > 0 && (
              <div className="text-sm text-muted-foreground">
                {credits.reserved_credits} reserved
              </div>
            )}
          </div>
        ) : creditsError ? (
          <div className="text-sm text-danger">Unable to load audio/video credits.</div>
        ) : creditsLoading ? (
          <div className="text-sm text-muted-foreground">Loading...</div>
        ) : null}
      </div>

      <div className="space-y-4">
        {/* Organization ID (read-only, copyable) */}
        <div>
          <label htmlFor="org-id" className="block text-sm font-medium text-muted-foreground mb-1">
            Organization ID
          </label>
          <div className="flex gap-2">
            <input
              id="org-id"
              type="text"
              readOnly
              value={activeOrg.id}
              className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-muted-foreground text-sm font-mono select-all focus:outline-none focus:ring-2 focus:ring-volt/50 focus:border-volt"
            />
            <button
              type="button"
              onClick={handleCopyId}
              aria-label="Copy organization ID"
              className="shrink-0 px-3 py-2 rounded-lg bg-surface border border-border text-muted-foreground hover:text-foreground hover:bg-surface-hover transition-colors"
            >
              {idCopied ? (
                <CheckIcon className="w-4 h-4 text-success" />
              ) : (
                <CopyIcon className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>

        {/* Name */}
        <div>
          <label htmlFor="org-name" className="block text-sm font-medium text-muted-foreground mb-1">
            Organization Name
          </label>
          <input
            id="org-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={!canManageSettings}
            className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-volt/50 focus:border-volt disabled:opacity-50 disabled:cursor-not-allowed"
            placeholder="Acme Corp"
          />
        </div>

        {/* Website URL */}
        <div>
          <label htmlFor="org-website" className="block text-sm font-medium text-muted-foreground mb-1">
            Website
          </label>
          <input
            id="org-website"
            type="url"
            value={websiteUrl}
            onChange={(e) => setWebsiteUrl(e.target.value)}
            disabled={!canManageSettings}
            className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-volt/50 focus:border-volt disabled:opacity-50 disabled:cursor-not-allowed"
            placeholder="https://example.com"
          />
        </div>

        {/* Logo URL */}
        <div>
          <label htmlFor="org-logo" className="block text-sm font-medium text-muted-foreground mb-1">
            Logo URL
          </label>
          <input
            id="org-logo"
            type="url"
            value={logoUrl}
            onChange={(e) => setLogoUrl(e.target.value)}
            disabled={!canManageSettings}
            className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-volt/50 focus:border-volt disabled:opacity-50 disabled:cursor-not-allowed"
            placeholder="https://example.com/logo.png"
          />
          {logoUrl && (
            <div className="mt-2">
              <img
                src={logoUrl}
                alt="Org logo preview"
                className="w-12 h-12 rounded-lg object-cover border border-border"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            </div>
          )}
        </div>

        {/* holito referral toggle */}
        <div className="pt-2 border-t border-border">
          <div className="flex items-center gap-2 mb-2">
            <label className="block text-sm font-medium text-muted-foreground">
              Interview Branding
            </label>
            <FeatureGateBadge feature="remove_holito_referral" />
          </div>
          <Toggle
            enabled={showHolitoReferral}
            onChange={(val) => {
              if (can('remove_holito_referral')) setShowHolitoReferral(val);
            }}
            label="Show holito referral"
            description="Display 'Try holito free' on the completion screen"
            disabled={!can('remove_holito_referral')}
          />
        </div>

      </div>

      {/* Sticky save bar - pinned to the bottom of the viewport while the
          Organization section is on screen. Mirrors the pattern used in
          WorkspaceSettings. Disabled + tooltip for non-managers. */}
      <div className="sticky bottom-0 z-10 mt-6 -mx-4 px-4 py-3 border-t border-border bg-background/95 backdrop-blur-sm flex items-center justify-end gap-3">
        <div className="min-w-0 flex-1 text-right">
          <AnimatedPresence show={!!saveMessage}>
            <AnimatedLabel text={saveMessage ?? ''} multiline className={`text-sm transition-colors ${saveMessage?.includes('Failed') ? 'text-danger' : 'text-success'}`} />
          </AnimatedPresence>
        </div>
        <Tooltip content={readOnlyTooltip} position="top">
          <button aria-busy={saving}
            onClick={handleSave}
            disabled={saving || !canManageSettings}
            className="px-6 py-2 button-accent font-bold text-sm rounded-lg transition-[background-color,color,opacity] duration-200 motion-reduce:transition-none disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ButtonContent loading={saving}>Save Changes</ButtonContent>
          </button>
        </Tooltip>
      </div>
    </div>
  );
};
